import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { getBranch, getBranchWithPhases, GROUP_PHASE_AVG } from '../../utils/orgData';
import { STAGES } from '../../utils/constants';
import { PG } from '../../utils/theme';
import FitmentFlowEmbed from './FitmentFlowEmbed';
import FleetBranchView from './FleetBranchView';

const { FiActivity, FiClock, FiTool, FiAlertCircle, FiTruck, FiLayers } = FiIcons;

const adhColor = (v) => (v >= 95 ? PG.emerald : v >= 90 ? PG.amber : PG.red);
const adhTone  = (v) => (v >= 95 ? 'ok' : v >= 90 ? 'warn' : 'alert');

const Sparkline = ({ data, color = PG.emerald, width = 84, height = 22 }) => {
  if (!data?.length) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * stepX},${height - ((v - min) / range) * height}`).join(' ');
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
};

const TabButton = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`h-9 px-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.22em] font-bold transition-all ${
      active
        ? 'bg-[#D71921] text-white'
        : 'bg-transparent text-neutral-500 hover:text-white hover:bg-white/5'
    }`}
  >
    {Icon && <Icon className="text-sm" />}
    {children}
  </button>
);

function PhaseBreakdown({ branch }) {
  if (!branch) return null;
  const rows = STAGES.map((s) => {
    const p = branch.phasePerformance[s.id];
    const delta = +(p.actualAvgMin - p.slaMin).toFixed(1);
    const deltaPct = Math.round((delta / p.slaMin) * 100);
    const group = GROUP_PHASE_AVG[s.id];
    return { stage: s, p, delta, deltaPct, group };
  });

  const breached = rows.filter((r) => r.p.adherencePct < 90).length;
  const onTrack  = rows.filter((r) => r.p.adherencePct >= 95).length;
  const worst    = [...rows].sort((a, b) => a.p.adherencePct - b.p.adherencePct)[0];
  const best     = [...rows].sort((a, b) => b.p.adherencePct - a.p.adherencePct)[0];

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-4 flex-shrink-0">
        <KPICard
          title="Phases_On_Track"
          value={onTrack}
          unit={`/ ${STAGES.length}`}
          target="≥5"
          trend={onTrack >= 5 ? 'up' : 'down'}
          trendValue={onTrack >= 5 ? 'Healthy' : 'Below'}
          icon={FiActivity}
          accent={onTrack >= 5 ? PG.emerald : PG.amber}
        />
        <KPICard
          title="Phases_Breached"
          value={breached}
          unit={`/ ${STAGES.length}`}
          target="0"
          trend={breached === 0 ? 'up' : 'down'}
          trendValue={breached === 0 ? 'Clean' : 'Flagged'}
          icon={FiAlertCircle}
          accent={breached === 0 ? PG.emerald : PG.red}
        />
        <KPICard
          title="Worst_Phase"
          value={worst.stage.name}
          unit={`${worst.p.adherencePct}%`}
          target="≥95%"
          trend="down"
          trendValue={`+${worst.deltaPct}% over`}
          icon={FiClock}
          accent={adhColor(worst.p.adherencePct)}
        />
        <KPICard
          title="Best_Phase"
          value={best.stage.name}
          unit={`${best.p.adherencePct}%`}
          target="≥95%"
          trend="up"
          trendValue={best.deltaPct <= 0 ? 'Inside' : `+${best.deltaPct}%`}
          icon={FiTool}
          accent={adhColor(best.p.adherencePct)}
        />
      </div>

      {/* Phase breakdown table */}
      <Panel
        title="Phase_SLA_vs_Actual"
        subtitle={`${branch.name} // 7 phases // vs Group average`}
        right={<StatusPill tone={adhTone(branch.ops.slaAdherence)}>{branch.ops.slaAdherence}% overall</StatusPill>}
        className="flex-1 min-h-0"
      >
        <div className="h-full overflow-y-auto custom-scrollbar">
          <table className="w-full text-left">
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="border-b border-white/10">
                <th className="px-4 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">Phase</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">SLA (min)</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Actual (min)</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Delta</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Group_Avg</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Adherence</th>
                <th className="px-4 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">12M_Trend</th>
                <th className="px-3 py-2 font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ stage, p, delta, deltaPct, group }) => (
                <tr key={stage.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-2">
                    <div className="font-mono-tech text-[11px] uppercase tracking-wider text-white">{stage.name}</div>
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-600">{stage.id}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-doto text-base text-neutral-300">{p.slaMin}</td>
                  <td className="px-3 py-2 text-right font-doto text-base" style={{ color: delta <= 0 ? PG.emerald : delta <= p.slaMin * 0.15 ? PG.amber : PG.red }}>
                    {p.actualAvgMin}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-tech text-[11px]" style={{ color: delta <= 0 ? PG.emerald : PG.red }}>
                    {delta > 0 ? '+' : ''}{delta}m ({deltaPct > 0 ? '+' : ''}{deltaPct}%)
                  </td>
                  <td className="px-3 py-2 text-right font-doto text-base text-neutral-400">
                    {group.actualAvgMin}
                    <span className="ml-1 font-mono-tech text-[9px] text-neutral-600">
                      ({group.adherencePct}%)
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="font-doto text-base" style={{ color: adhColor(p.adherencePct) }}>
                      {p.adherencePct}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Sparkline data={p.trend} color={adhColor(p.adherencePct)} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <StatusPill tone={adhTone(p.adherencePct)}>
                      {p.adherencePct >= 95 ? 'On_Track' : p.adherencePct >= 90 ? 'Warning' : 'Breached'}
                    </StatusPill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export default function OpsBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);
  const [view, setView] = useState('jobs');

  if (!branch) {
    return (
      <div className="h-full flex items-center justify-center flex-col text-neutral-500">
        <div className="font-doto text-4xl text-white mb-2">Branch_Not_Found</div>
        <Link to="/sliver/ops" className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#D71921] hover:text-white">
          ← Back to Operations
        </Link>
      </div>
    );
  }

  const o = branch.ops;
  const bp = getBranchWithPhases(branchId);

  const subtitle = view === 'jobs'
    ? 'JOBS & SLA'
    : view === 'fleet'
    ? 'FLEET & ASSETS'
    : 'PHASE SLA BREAKDOWN';

  return (
    <div className="h-full flex flex-col gap-4 min-h-0">
      {/* View tab bar */}
      <div className="flex items-center flex-shrink-0 border-b border-white/5 pb-3">
        <div className="inline-flex border border-white/10">
          <TabButton active={view === 'jobs'} onClick={() => setView('jobs')} icon={FiActivity}>
            Jobs_Flow
          </TabButton>
          <TabButton active={view === 'phases'} onClick={() => setView('phases')} icon={FiLayers}>
            Phase_SLA
          </TabButton>
          <TabButton active={view === 'fleet'} onClick={() => setView('fleet')} icon={FiTruck}>
            Fleet_Ops
          </TabButton>
        </div>
        <div className="ml-auto font-mono-tech text-[9px] uppercase tracking-[0.28em] text-neutral-600">
          {branch.name.toUpperCase()} // {subtitle}
        </div>
      </div>

      {view === 'fleet' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <FleetBranchView branchId={branchId} />
        </div>
      ) : view === 'phases' ? (
        <PhaseBreakdown branch={bp} />
      ) : (
        <>
      {/* Top KPI strip */}
      <div className="grid grid-cols-4 gap-4 flex-shrink-0">
        <KPICard
          title="Jobs_Today"
          value={o.jobsToday}
          unit="jobs"
          target="-"
          trend="up"
          trendValue="Live"
          icon={FiActivity}
          accent={PG.cyan}
        />
        <KPICard
          title="Avg_Turnaround"
          value={o.avgTurnaroundMin}
          unit="min"
          target="≤50"
          trend={o.avgTurnaroundMin <= 50 ? 'up' : 'down'}
          trendValue={o.avgTurnaroundMin <= 50 ? 'Inside' : 'Over'}
          icon={FiClock}
          accent={PG.amber}
        />
        <KPICard
          title="First_Time_Fix"
          value={`${o.firstTimeFix}%`}
          target="≥95%"
          trend={o.firstTimeFix >= 95 ? 'up' : 'down'}
          trendValue={o.firstTimeFix >= 95 ? '+0.3%' : '-0.6%'}
          icon={FiTool}
          accent={PG.emerald}
        />
        <KPICard
          title="SLA_Breached"
          value={o.breachedSlaCount}
          unit="jobs"
          target="0"
          trend={o.breachedSlaCount === 0 ? 'up' : 'down'}
          trendValue={o.breachedSlaCount === 0 ? 'Clean' : 'Flagged'}
          icon={FiAlertCircle}
          accent={o.breachedSlaCount === 0 ? PG.emerald : PG.red}
        />
      </div>

      {/* Embedded Fitment Flow */}
      <div className="flex-1 min-h-0">
        <FitmentFlowEmbed />
      </div>
        </>
      )}
    </div>
  );
}
